import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Megaphone, Plus, X, MagnifyingGlass } from '@phosphor-icons/react';
import { toast } from 'sonner';

const SOURCES = ['meta_ads', 'google_ads', 'whatsapp', 'walk_in', 'referral', 'just_dial', 'website', 'event', 'phone', 'other'];
const STATUSES = ['active', 'paused', 'completed', 'draft'];

const STATUS_COLOR = {
  active: '#10A882',
  paused: '#E5A100',
  completed: '#2B6EF0',
  draft: '#808080',
};

const fmtMoney = (n) => `Rs.${(Number(n) || 0).toLocaleString('en-IN')}`;
const fmtNum = (n) => (Number(n) || 0).toLocaleString('en-IN');
const pct = (n) => `${(Number(n) || 0).toFixed(1)}%`;

export default function CampaignsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [branches, setBranches] = useState([]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/analytics/campaign-performance');
      setRows(data.campaigns || data.rows || data || []);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  useEffect(() => {
    api.get('/api/branches')
      .then((r) => setBranches(r.data.branches || r.data || []))
      .catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (sourceFilter !== 'all' && r.source !== sourceFilter) return false;
      if (search && !`${r.name || ''}`.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [rows, statusFilter, sourceFilter, search]);

  const handleCreated = () => {
    setShowCreate(false);
    fetchRows();
  };

  return (
    <div data-testid="campaigns-page" className="stagger-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#E04B2A15', color: '#E04B2A' }}>
            <Megaphone size={20} weight="fill" />
          </div>
          <div>
            <h1 className="font-display text-3xl font-extrabold text-foreground tracking-tight">Campaigns</h1>
            <p className="text-sm text-muted-foreground mt-1">
              <span className="font-mono">{rows.length}</span> total &middot; marketing attribution & performance
            </p>
          </div>
        </div>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button data-testid="new-campaign-btn" className="h-9 rounded-lg text-xs gap-1.5 font-bold text-white" style={{ backgroundColor: '#E04B2A' }}>
              <Plus size={14} weight="bold" /> New Campaign
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-xl max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display text-foreground text-lg">New Campaign</DialogTitle></DialogHeader>
            <CampaignForm branches={branches} onCreated={handleCreated} />
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="campaign-filters">
        <div className="relative flex-1 max-w-xs">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search campaigns..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="campaign-search"
            className="pl-9 h-9 rounded-lg text-xs"
          />
        </div>
        <div className="flex items-center gap-1 border border-border rounded-lg p-0.5 bg-card">
          {['all', ...STATUSES].map((s) => (
            <button
              key={s}
              data-testid={`campaign-status-${s}`}
              onClick={() => setStatusFilter(s)}
              className="text-[11px] px-3 py-1 rounded-md font-semibold uppercase tracking-wider transition-colors"
              style={{
                backgroundColor: statusFilter === s ? '#E04B2A' : 'transparent',
                color: statusFilter === s ? '#fff' : 'hsl(var(--muted-foreground))',
              }}
            >
              {s}
            </button>
          ))}
        </div>
        <select
          data-testid="campaign-source-filter"
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          className="h-9 rounded-lg text-xs border border-border px-3 bg-card text-foreground font-mono"
        >
          <option value="all">All Sources</option>
          {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Table */}
      <Card className="border border-border rounded-xl overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-left" data-testid="campaigns-table">
              <thead>
                <tr className="bg-secondary/50 border-b border-border">
                  <th className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold py-2.5 px-4 font-mono">Name</th>
                  <th className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold py-2.5 px-4 font-mono">Source / Platform</th>
                  <th className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold py-2.5 px-4 font-mono">Status</th>
                  <th className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold py-2.5 px-4 font-mono text-right">Budget</th>
                  <th className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold py-2.5 px-4 font-mono text-right">Spend</th>
                  <th className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold py-2.5 px-4 font-mono text-right">Leads</th>
                  <th className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold py-2.5 px-4 font-mono text-right">Enrolled</th>
                  <th className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold py-2.5 px-4 font-mono text-right">Conv.</th>
                  <th className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold py-2.5 px-4 font-mono text-right">CPL</th>
                  <th className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold py-2.5 px-4 font-mono text-right">ROAS</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={10} className="text-center text-xs text-muted-foreground h-32 font-mono">loading...</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={10} className="text-center text-xs text-muted-foreground h-32">No campaigns yet. Create one to start tracking.</td></tr>
                ) : filtered.map((c) => {
                  const id = c.id || c._id || c.campaign_id;
                  const statusColor = STATUS_COLOR[c.status] || '#808080';
                  return (
                    <tr
                      key={id}
                      data-testid={`campaign-row-${id}`}
                      onClick={() => navigate(`/leads?campaign_id=${id}`)}
                      className="border-b border-border last:border-0 cursor-pointer hover:bg-secondary/40 transition-colors"
                    >
                      <td className="py-2.5 px-4">
                        <div className="text-[12px] font-semibold text-foreground">{c.name || '-'}</div>
                        {c.objective && <div className="text-[10px] text-muted-foreground font-mono mt-0.5">{c.objective}</div>}
                      </td>
                      <td className="py-2.5 px-4">
                        <div className="text-[11px] text-foreground/80 font-mono">{c.source || '-'}</div>
                        {c.platform && <div className="text-[10px] text-muted-foreground font-mono">{c.platform}</div>}
                      </td>
                      <td className="py-2.5 px-4">
                        <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider"
                          style={{ color: statusColor }}>
                          <span className="w-1.5 h-1.5 rounded-sm" style={{ backgroundColor: statusColor }} />
                          {c.status || 'draft'}
                        </span>
                      </td>
                      <td className="py-2.5 px-4 text-right text-[11px] text-foreground font-mono">{fmtMoney(c.budget)}</td>
                      <td className="py-2.5 px-4 text-right text-[11px] text-foreground font-mono">{fmtMoney(c.spend)}</td>
                      <td className="py-2.5 px-4 text-right text-[11px] text-foreground font-mono">{fmtNum(c.leads)}</td>
                      <td className="py-2.5 px-4 text-right text-[11px] font-mono" style={{ color: '#10A882' }}>{fmtNum(c.enrolled)}</td>
                      <td className="py-2.5 px-4 text-right text-[11px] text-foreground font-mono font-semibold">{pct(c.conversion_rate)}</td>
                      <td className="py-2.5 px-4 text-right text-[11px] text-foreground font-mono">{fmtMoney(c.cpl)}</td>
                      <td className="py-2.5 px-4 text-right text-[11px] font-mono font-semibold" style={{ color: (Number(c.roas) || 0) >= 1 ? '#10A882' : '#EF4444' }}>
                        {(Number(c.roas) || 0).toFixed(2)}x
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function CampaignForm({ branches, onCreated }) {
  const [form, setForm] = useState({
    name: '',
    campaign_id: '',
    source: 'meta_ads',
    platform: '',
    objective: 'leads',
    budget: '',
    target_cpl: '',
    start_date: '',
    end_date: '',
    branch_id: '',
    status: 'active',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        budget: form.budget ? Number(form.budget) : 0,
        target_cpl: form.target_cpl ? Number(form.target_cpl) : 0,
      };
      await api.post('/api/campaigns', payload);
      toast.success('Campaign created');
      onCreated();
    } catch (err) {
      toast.error('Failed to create campaign');
    } finally {
      setSubmitting(false);
    }
  };

  const Field = ({ label, children, required }) => (
    <div>
      <Label className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">
        {label}{required && ' *'}
      </Label>
      {children}
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-3" data-testid="campaign-form">
      <Field label="Name" required>
        <Input data-testid="campaign-name" value={form.name} onChange={(e) => update('name', e.target.value)} className="h-9 rounded-lg text-sm" required />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Campaign ID (external)">
          <Input data-testid="campaign-extid" value={form.campaign_id} onChange={(e) => update('campaign_id', e.target.value)} className="h-9 rounded-lg text-sm font-mono" placeholder="120211..." />
        </Field>
        <Field label="Status">
          <select data-testid="campaign-status" value={form.status} onChange={(e) => update('status', e.target.value)} className="h-9 w-full rounded-lg text-sm border border-border px-3 bg-card text-foreground">
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Source">
          <select data-testid="campaign-source" value={form.source} onChange={(e) => update('source', e.target.value)} className="h-9 w-full rounded-lg text-sm border border-border px-3 bg-card text-foreground">
            {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Platform">
          <Input data-testid="campaign-platform" value={form.platform} onChange={(e) => update('platform', e.target.value)} className="h-9 rounded-lg text-sm" placeholder="facebook, google_search..." />
        </Field>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Objective">
          <Input data-testid="campaign-objective" value={form.objective} onChange={(e) => update('objective', e.target.value)} className="h-9 rounded-lg text-sm" placeholder="leads" />
        </Field>
        <Field label="Budget (Rs.)">
          <Input data-testid="campaign-budget" type="number" value={form.budget} onChange={(e) => update('budget', e.target.value)} className="h-9 rounded-lg text-sm font-mono" />
        </Field>
        <Field label="Target CPL (Rs.)">
          <Input data-testid="campaign-target-cpl" type="number" value={form.target_cpl} onChange={(e) => update('target_cpl', e.target.value)} className="h-9 rounded-lg text-sm font-mono" />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Start Date">
          <Input data-testid="campaign-start" type="date" value={form.start_date} onChange={(e) => update('start_date', e.target.value)} className="h-9 rounded-lg text-sm font-mono" />
        </Field>
        <Field label="End Date">
          <Input data-testid="campaign-end" type="date" value={form.end_date} onChange={(e) => update('end_date', e.target.value)} className="h-9 rounded-lg text-sm font-mono" />
        </Field>
      </div>
      <Field label="Branch">
        <select data-testid="campaign-branch" value={form.branch_id} onChange={(e) => update('branch_id', e.target.value)} className="h-9 w-full rounded-lg text-sm border border-border px-3 bg-card text-foreground font-mono">
          <option value="">All branches</option>
          {branches.map((b) => <option key={b.id || b._id} value={b.id || b._id}>{b.name}</option>)}
        </select>
      </Field>
      <Field label="Notes">
        <textarea data-testid="campaign-notes" value={form.notes} onChange={(e) => update('notes', e.target.value)} rows={2}
          className="w-full rounded-lg text-sm border border-border px-3 py-2 bg-card text-foreground resize-none" />
      </Field>
      <div className="flex justify-end pt-1">
        <Button type="submit" disabled={submitting} data-testid="submit-campaign-btn" className="h-9 rounded-lg text-xs font-bold px-4 text-white" style={{ backgroundColor: '#E04B2A' }}>
          {submitting ? 'Creating...' : 'Create Campaign'}
        </Button>
      </div>
    </form>
  );
}
